import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, of, Observable } from 'rxjs';
import { takeUntil, switchMap, map, catchError } from 'rxjs/operators';
import { StockMovementsService } from '../../../../services/stock-movements.service';
import { StockService } from '../../../../services/stock.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import { ItemService } from '../../../../services/item.service';
import { StaffService } from '../../../../services/staff.service';
import { ErrorService } from '../../../../shared/services/error.service';
import { StockMovement, StockMovementUpdate } from '../../../../models/StockMovement';
import { Stock } from '../../../../models/Stock';
import { Warehouse } from '../../../../models/Warehouse';
import { Item } from '../../../../models/Item';
import { StaffMember } from '../../../../models/StaffMember';
import {
  canLinkPendingItemToStockEntry,
  resolvePendingItemEntryIdForLink
} from '../stock-movement-pending-item-link.helper';

@Component({
  selector: 'app-stock-movements-form',
  templateUrl: './stock-movements-form.component.html',
  styleUrls: ['./stock-movements-form.component.css']
})
export class StockMovementsFormComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  movementForm: FormGroup;
  isEditMode = false;
  isViewMode = false;
  isLoading = false;
  isNewExitFlow = false;
  movementId: number | null = null;
  movement: StockMovement | null = null;

  /** Modal: vínculo item de estoque (entrada pendente). */
  showLinkItemModal = false;
  linkItemModalPendingEntryId: number | null = null;
  linkItemModalContext = '';
  linkItemSelectedId: number | null = null;
  linkItemSearchTerm = '';
  linkItemSearchResults: Item[] = [];
  linkItemSearchLoading = false;
  linkItemSubmitting = false;

  // Dados para dropdowns
  items: Item[] = [];
  itemsTotalItems = 0;
  itemsTotalPages = 0;
  itemsCurrentPage = 1;
  itemsPageSize = 10;
  itemSearchTerm = '';
  warehouses: Warehouse[] = [];
  staffMembers: StaffMember[] = [];

  // Opções para os dropdowns
  movementTypes = [
    { value: 'Entrada', label: 'Entrada' },
    { value: 'Saida', label: 'Saída' }
  ];
  exitTypes = [
    { value: 'Padrao', label: 'Saída Manual Padrão' },
    { value: 'Consumo', label: 'Saída de Item de Consumo' }
  ];
  movementSources = [
    { value: 'manual', label: 'Manual (sem vínculo)' },
    { value: 'serviceOrder', label: 'Vinculada a OS' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private stockMovementsService: StockMovementsService,
    private stockService: StockService,
    private warehousesService: WarehousesService,
    private itemService: ItemService,
    private staffService: StaffService,
    private errorService: ErrorService
  ) {
    this.movementForm = this.createForm();
  }

  ngOnInit(): void {
    this.isViewMode = (this.route.snapshot.routeConfig?.path || '').includes('/view');
    this.loadInitialData();
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      itemId: ['', [Validators.required]],
      movementType: ['', [Validators.required]],
      quantity: ['', [Validators.required, Validators.min(0.01)]],
      movementDate: ['', [Validators.required]],
      movementSource: ['manual', [Validators.required]],
      originWarehouseId: [''],
      destinationWarehouseId: [''],
      exitType: ['Padrao'],
      reason: ['', [Validators.required]],
      notes: [''],
      staffId: [''],
      status: [''],
      requiresApproval: [false],
      attachmentsText: [''],
      serviceOrderId: ['']
    });
  }

  private initializeForm(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.isEditMode = !this.isViewMode;
        this.movementId = +params['id'];
        this.loadMovement();
      } else {
        this.isEditMode = false;
        const queryType = this.route.snapshot.queryParamMap.get('tipo');
        this.isNewExitFlow = queryType?.toLowerCase() === 'saida';
        this.setDefaultValues();
      }
    });
  }

  private setDefaultValues(): void {
    const today = new Date().toISOString().split('T')[0];
    const queryType = this.route.snapshot.queryParamMap.get('tipo');
    const queryFlow = this.route.snapshot.queryParamMap.get('fluxo');
    const isSaida = queryType?.toLowerCase() === 'saida';
    const isConsumoExit = isSaida && (queryFlow?.toLowerCase() !== 'padrao');
    const serviceOrderId = this.route.snapshot.queryParamMap.get('serviceOrderId');
    const source = serviceOrderId ? 'serviceOrder' : 'manual';

    this.movementForm.patchValue({
      movementDate: today,
      movementSource: source,
      movementType: isSaida ? 'Saida' : 'Entrada',
      exitType: isSaida ? (isConsumoExit ? 'Consumo' : 'Padrao') : 'Padrao',
      reason: isSaida ? 'Consumo OS' : '',
      notes: isSaida ? 'Saida manual' : '',
      requiresApproval: isSaida,
      serviceOrderId: serviceOrderId ? Number(serviceOrderId) : ''
    });
    this.onMovementConfigChange();
  }

  private loadInitialData(): void {
    this.loadItems();
    this.loadWarehouses();
    this.loadStaffMembers();
  }

  private loadItems(nameFilter?: string): void {
    const filter = (nameFilter ?? this.itemSearchTerm ?? '').trim();
    const request$ = filter
      ? this.itemService.getAllItemsWithNameFilter(filter, { page: 1, pageSize: this.itemsPageSize })
      : this.itemService.getAllItems({ page: 1, pageSize: this.itemsPageSize });

    request$.subscribe({
      next: (result) => {
        this.items = result.items || [];
        this.itemsTotalItems = Number(result.totalItems || 0);
        this.itemsTotalPages = Number(result.totalPages || 0);
        this.itemsCurrentPage = Number(result.page || 1);
      },
      error: (error) => {
        console.error('Erro ao carregar itens:', error);
        this.items = [];
        this.itemsTotalItems = 0;
        this.itemsTotalPages = 0;
        this.itemsCurrentPage = 1;
      }
    });
  }

  onItemSearchChange(): void {
    this.movementForm.patchValue({ itemId: '' }, { emitEvent: false });
  }

  searchItems(): void {
    this.loadItems(this.itemSearchTerm);
  }

  selectSearchedItem(item: Item): void {
    this.movementForm.patchValue({ itemId: item.id }, { emitEvent: false });
    this.itemSearchTerm = `${item.code} - ${item.name}`;
  }

  clearSelectedItem(): void {
    this.itemSearchTerm = '';
    this.movementForm.patchValue({ itemId: '' }, { emitEvent: false });
    this.loadItems();
  }

  private loadWarehouses(): void {
    this.warehousesService.getAll({ page: 1, pageSize: 1000 }).subscribe({
      next: (result) => {
        this.warehouses = result.items || [];
      },
      error: (error) => {
        console.error('Erro ao carregar almoxarifados:', error);
        this.warehouses = [];
      }
    });
  }

  private loadStaffMembers(): void {
    this.staffService.getStaff().subscribe({
        next: (staff: StaffMember[]) => {
        this.staffMembers = staff || [];
      },
      error: (error: any) => {
        console.error('Erro ao carregar funcionários:', error);
        this.staffMembers = [];
      }
    });
  }

  private loadMovement(): void {
    if (!this.movementId) return;

    this.isLoading = true;
    this.stockMovementsService
      .getById(this.movementId)
      .pipe(
        switchMap((movement) => this.hydrateMovementDetailForView(movement)),
        takeUntil(this.destroy$)
      )
      .subscribe({
      next: (movement: StockMovement) => {
        this.movement = movement;
        this.populateForm(movement);
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Erro ao carregar movimentação:', error);
        this.isLoading = false;
        this.router.navigate(['/stock/movements']);
      }
    });
  }

  /**
   * Preenche nome/código do item (cadastro Itens) e nomes de almoxarifados quando a API
   * devolve só ids — evita «Não informado» na visualização.
   */
  private hydrateMovementDetailForView(m: StockMovement): Observable<StockMovement> {
    return of(m).pipe(
      switchMap((x) => {
        if (x.itemId == null || x.itemId <= 0) {
          return of(x);
        }
        return this.itemService.getItemById(x.itemId).pipe(
          map((it) => ({
            ...x,
            itemName: (it.name || '').trim() || (x.itemName || '').trim() || x.itemName,
            itemCode: (it.code || '').trim() || (x.itemCode || '').trim() || x.itemCode
          })),
          catchError(() => of(x))
        );
      }),
      switchMap((x) => {
        const originOk = (x.originWarehouseName || '').trim() && (x.originWarehouseName || '').trim() !== '-';
        if (originOk) {
          return of(x);
        }
        if (x.originWarehouseId != null && x.originWarehouseId > 0) {
          return this.warehousesService.getById(x.originWarehouseId).pipe(
            map((w) => ({ ...x, originWarehouseName: w?.name || x.originWarehouseName })),
            catchError(() => of(x))
          );
        }
        return of(x);
      }),
      switchMap((x) => {
        const destOk = (x.destinationWarehouseName || '').trim() && (x.destinationWarehouseName || '').trim() !== '-';
        if (destOk) {
          return of(x);
        }
        if (x.destinationWarehouseId != null && x.destinationWarehouseId > 0) {
          return this.warehousesService.getById(x.destinationWarehouseId).pipe(
            map((w) => ({ ...x, destinationWarehouseName: w?.name || x.destinationWarehouseName })),
            catchError(() => of(x))
          );
        }
        return of(x);
      })
    );
  }

  private populateForm(movement: StockMovement): void {
    const movementTypeLabel = this.mapMovementTypeToLabel(movement.movementType);
    this.itemSearchTerm = movement.itemCode && movement.itemName
      ? `${movement.itemCode} - ${movement.itemName}`
      : (movement.itemName || '');
    this.movementForm.patchValue({
      itemId: movement.itemId != null && movement.itemId > 0 ? movement.itemId : '',
      movementType: movementTypeLabel,
      quantity: movement.quantity,
      movementDate: movement.movementDate ? movement.movementDate.split('T')[0] : '',
      originWarehouseId: movement.originWarehouseId || '',
      destinationWarehouseId: movement.destinationWarehouseId || '',
      exitType: movement.movementType === 'Exit' ? 'Padrao' : 'Padrao',
      reason: movement.reason ?? '',
      notes: movement.notes || '',
      staffId: movement.staffId != null && movement.staffId > 0 ? movement.staffId : '',
      status: movement.status,
      requiresApproval: movement.requiresApproval,
      attachmentsText: movement.fiscalDocument || '',
      serviceOrderId: movement.serviceOrderId || ''
    });
    this.onMovementConfigChange();
    if (this.isViewMode) {
      this.movementForm.disable({ emitEvent: false });
    }
  }

  onSubmit(): void {
    if (this.isViewMode) {
      return;
    }

    if (this.movementForm.valid) {
      this.isLoading = true;
      
      const formData = this.movementForm.value;
      const attachments: string[] = [];

      if (this.isEditMode && this.movementId) {
        this.updateMovement({
          itemId: formData.itemId,
          movementType: this.mapMovementTypeToApi(formData.movementType),
          quantity: parseFloat(formData.quantity),
          movementDate: new Date(formData.movementDate).toISOString(),
          originWarehouseId: formData.originWarehouseId || undefined,
          destinationWarehouseId: formData.destinationWarehouseId || undefined,
          reason: formData.reason,
          notes: formData.notes || undefined,
          staffId: formData.staffId ? Number(formData.staffId) : undefined,
          requiresApproval: formData.requiresApproval,
          serviceOrderId: formData.serviceOrderId || undefined
        });
      } else {
        this.createMovement({
          movementType: formData.movementType as 'Entrada' | 'Saida',
          movementSource: formData.movementSource as 'manual' | 'serviceOrder',
          exitType: (formData.exitType || 'Padrao') as 'Padrao' | 'Consumo',
          itemId: Number(formData.itemId),
          quantity: Number(formData.quantity),
          originWarehouseId: formData.originWarehouseId ? Number(formData.originWarehouseId) : null,
          destinationWarehouseId: formData.destinationWarehouseId ? Number(formData.destinationWarehouseId) : null,
          reason: formData.reason,
          notes: formData.notes || null,
          staffId: formData.staffId ? Number(formData.staffId) : undefined,
          requiresApproval: formData.requiresApproval,
          serviceOrderId: formData.serviceOrderId ? Number(formData.serviceOrderId) : undefined
        }, attachments);
      }
    } else {
      this.markFormGroupTouched(this.movementForm);
    }
  }

  private createMovement(
    data: {
      movementType: 'Entrada' | 'Saida';
      movementSource: 'manual' | 'serviceOrder';
      exitType?: 'Padrao' | 'Consumo';
      itemId: number;
      quantity: number;
      originWarehouseId?: number | null;
      destinationWarehouseId?: number | null;
      reason: string;
      notes?: string | null;
      staffId?: number;
      requiresApproval?: boolean;
      serviceOrderId?: number;
    },
    attachments: string[]
  ): void {
    const request$ = data.movementSource === 'manual'
      ? this.stockMovementsService.createManualBatch({
          direction: data.movementType === 'Entrada' ? 'Entry' : 'Exit',
          requiresApproval: data.movementType === 'Saida' ? (data.requiresApproval ?? true) : false,
          notes: data.notes || data.reason,
          lines: [
            {
              itemId: Number(data.itemId),
              warehouseId: Number(data.movementType === 'Entrada' ? data.destinationWarehouseId : data.originWarehouseId),
              quantity: Number(data.quantity),
              note: data.reason
            }
          ]
        })
      : this.stockMovementsService.createManualHybrid(data, attachments);

    request$.subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(
          data.movementType === 'Saida' ? ['/stock/movements/todas'] : ['/stock/movements']
        );
      },
      error: (error) => {
        console.error('Erro ao criar movimentação:', error);
        this.isLoading = false;
      }
    });
  }

  private updateMovement(data: StockMovementUpdate): void {
    if (!this.movementId) return;

    this.stockMovementsService.update(this.movementId, data).subscribe({
      next: () => {
        this.isLoading = false;
        this.router.navigate(['/stock/movements']);
      },
      error: (error) => {
        console.error('Erro ao atualizar movimentação:', error);
        this.isLoading = false;
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/stock/movements']);
  }

  canRegisterEntryReceipt(m: StockMovement | null): boolean {
    if (!m) {
      return false;
    }
    return this.stockMovementsService.isOpenForEntryReceipt(m);
  }

  goToRegisterReceipt(): void {
    if (this.movementId) {
      this.router.navigate(['/stock/movements', this.movementId, 'confirm-receipt']);
    }
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  // Helpers para validação
  hasError(controlName: string, errorType: string): boolean {
    const control = this.movementForm.get(controlName);
    return !!(control?.hasError(errorType) && control?.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.movementForm.get(controlName);
    if (control?.hasError('required')) {
      return 'Este campo é obrigatório';
    }
    if (control?.hasError('min')) {
      return 'A quantidade deve ser maior que zero';
    }
    return '';
  }

  // Validações específicas por tipo de movimentação e origem da movimentação
  onMovementConfigChange(): void {
    const movementType = this.movementForm.get('movementType')?.value;
    const movementSource = this.movementForm.get('movementSource')?.value;
    const originControl = this.movementForm.get('originWarehouseId');
    const destinationControl = this.movementForm.get('destinationWarehouseId');
    const serviceOrderControl = this.movementForm.get('serviceOrderId');

    // Reset validators
    originControl?.clearValidators();
    destinationControl?.clearValidators();
    serviceOrderControl?.clearValidators();

    switch (movementType) {
      case 'Entrada':
        destinationControl?.setValidators([Validators.required]);
        this.movementForm.patchValue(
          { originWarehouseId: '', exitType: 'Padrao' },
          { emitEvent: false }
        );
        break;
      case 'Saida':
        /** Saída manual: baixa a partir de um almoxarifado; não utiliza destino (não é transferência). */
        originControl?.setValidators([Validators.required]);
        this.movementForm.patchValue(
          { destinationWarehouseId: '', exitType: this.movementForm.get('exitType')?.value || 'Consumo' },
          { emitEvent: false }
        );
        break;
    }

    if (movementSource === 'serviceOrder') {
      serviceOrderControl?.setValidators([Validators.required, Validators.min(1)]);
    }

    originControl?.updateValueAndValidity();
    destinationControl?.updateValueAndValidity();
    serviceOrderControl?.updateValueAndValidity();
  }

  get pageTitle(): string {
    if (this.isViewMode) {
      return 'Visualizar Movimentação';
    }
    return this.isEditMode ? 'Editar Movimentação' : 'Nova Movimentação';
  }

  get submitButtonText(): string {
    return this.isEditMode ? 'Atualizar' : 'Criar';
  }

  formatDateTime(dateValue?: string): string {
    if (!dateValue) {
      return '-';
    }
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }
    return date.toLocaleString('pt-BR');
  }

  formatBoolean(value: boolean | undefined): string {
    return value ? 'Sim' : 'Não';
  }

  getItemViewLabel(m: StockMovement): string {
    const name = (m.linkedItemName || m.itemName || m.stockName || '').trim();
    if (name) {
      return name;
    }
    const code = (m.linkedItemCode || m.itemCode || m.stockCode || '').trim();
    if (code) {
      return code;
    }
    if (m.itemId != null && m.itemId > 0) {
      return `Item #${m.itemId}`;
    }
    if (m.linkedItemId != null && m.linkedItemId > 0) {
      return `Item #${m.linkedItemId}`;
    }
    const hint = (m.reason || m.notes || '').trim();
    if (hint) {
      return hint.length > 80 ? `${hint.slice(0, 80)}…` : hint;
    }
    return 'Não informado';
  }

  formatWarehouseName(value?: string | null): string {
    const v = (value ?? '').trim();
    if (!v || v === '-') {
      return 'Não informado';
    }
    return v;
  }

  /** Subtítulo do cabeçalho: código e/ou nome do item após hidratação. */
  getViewHeaderItemSubtitle(m: StockMovement): string | null {
    const code = (m.itemCode || m.stockCode || '').trim();
    const name = (m.itemName || m.stockName || '').trim();
    const parts: string[] = [];
    if (code) {
      parts.push(`Código: ${code}`);
    }
    if (name) {
      parts.push(name);
    }
    return parts.length ? parts.join(' · ') : null;
  }

  /** Origem na rota: se vazio em entrada de compra, indica o almoxarifado de recebimento. */
  getViewRouteOriginDisplay(m: StockMovement): string {
    const o = this.formatWarehouseName(m.originWarehouseName);
    if (o !== 'Não informado') {
      return o;
    }
    if (m.movementType === 'Entry') {
      const d = this.formatWarehouseName(m.destinationWarehouseName);
      if (d !== 'Não informado') {
        return `${d} (recebimento)`;
      }
    }
    return o;
  }

  /** Ainda sem origem definida (só texto genérico de destino). */
  isViewRouteOriginFallback(m: StockMovement): boolean {
    return this.isWarehouseUninformed(m.originWarehouseName);
  }

  getStaffViewLabel(m: StockMovement): string {
    if (m.staffName?.trim()) {
      return m.staffName.trim();
    }
    if (m.staffId != null && m.staffId > 0) {
      return `ID ${m.staffId}`;
    }
    return 'Não informado';
  }

  getMovementTypeViewLabel(type?: string): string {
    if (!type) {
      return '-';
    }
    const map: { [key: string]: string } = {
      Entry: 'Entrada',
      Exit: 'Saída',
      Transfer: 'Transferência',
      Adjustment: 'Ajuste'
    };
    return map[type] ?? type;
  }

  getStatusViewLabel(status?: string): string {
    const map: { [key: string]: string } = {
      AwaitingReceipt: 'Aguardando Recebimento',
      RecParcialPend: 'Recebimento parcial pendente',
      ReceiptCompleted: 'Recebimento concluído',
      Received: 'Recebido',
      Executed: 'Executado',
      Cancelled: 'Cancelado',
      Pending: 'Pendente',
      Approved: 'Aprovado',
      Rejected: 'Rejeitado',
      AguardandoCad: 'Aguardando cadastro',
      InTransit: 'Em trânsito',
      EmTransito: 'Em trânsito',
      Solicitado: 'Solicitado'
    };
    if (status && map[status] === undefined) {
      const k = status.toLowerCase();
      if (k === 'recparcialpend') {
        return map['RecParcialPend'];
      }
      if (k === 'receiptcompleted') {
        return map['ReceiptCompleted'];
      }
      if (k === 'aguardandocad' || (k.includes('aguardando') && k.includes('cad'))) {
        return 'Aguardando cadastro';
      }
      if (k.includes('transito') || k.includes('intransit')) {
        return 'Em trânsito';
      }
    }
    return map[status || ''] || status || '-';
  }

  getTransferStatusViewLabel(status?: string): string {
    const map: { [key: string]: string } = {
      AwaitingReceipt: 'Aguardando Recebimento',
      ReceiptCompleted: 'Recebimento concluído',
      InTransit: 'Em Trânsito',
      Transferred: 'Transferido',
      Cancelled: 'Cancelado'
    };
    if (status && !map[status] && (status || '').toLowerCase() === 'receiptcompleted') {
      return map['ReceiptCompleted'];
    }
    return map[status || ''] || status || '-';
  }

  /** Estados finais do fluxo logístico no stepper */
  private isViewFlowFinal(m: StockMovement): boolean {
    const s = m.status;
    const ts = m.transferStatus;
    const sLower = (s || '').toLowerCase();
    return (
      s === 'Executed' ||
      s === 'Received' ||
      s === 'ReceiptCompleted' ||
      sLower === 'receiptcompleted' ||
      ts === 'Transferred'
    );
  }

  private isViewFlowMid(m: StockMovement): boolean {
    if (this.isViewFlowFinal(m)) {
      return false;
    }
    return this.stockMovementsService.isOpenForEntryReceipt(m);
  }

  getViewStepperProgress(m: StockMovement): number {
    if (m.status === 'Cancelled' || m.transferStatus === 'Cancelled') {
      return 0;
    }
    if (this.isViewFlowFinal(m)) {
      return 100;
    }
    if (this.isViewFlowMid(m)) {
      return 50;
    }
    return 18;
  }

  getViewStepKind(index: 0 | 1 | 2, m: StockMovement): 'done' | 'current' | 'upcoming' {
    if (index === 0) {
      if (canLinkPendingItemToStockEntry(m)) {
        return 'current';
      }
      return 'done';
    }
    if (index === 1) {
      if (this.isViewFlowFinal(m)) {
        return 'done';
      }
      if (this.isViewFlowMid(m)) {
        return 'current';
      }
      return 'upcoming';
    }
    if (index === 2) {
      return this.isViewFlowFinal(m) ? 'done' : 'upcoming';
    }
    return 'upcoming';
  }

  getViewStepperMidLabel(m: StockMovement): string {
    if (this.getViewStepKind(1, m) !== 'current') {
      return 'Em trânsito';
    }
    if ((m.status || '').toLowerCase() === 'recparcialpend') {
      return 'Recebimento parcial';
    }
    if (m.status === 'AwaitingReceipt' || m.transferStatus === 'AwaitingReceipt') {
      return 'Aguardando recebimento';
    }
    if (this.stockMovementsService.isEntryInTransitPhase(m) || m.transferStatus === 'InTransit') {
      return 'Em trânsito';
    }
    return 'Em trânsito';
  }

  /** Rota: usa `transferStatus` ou infere «em trânsito» a partir de `status` quando a API não duplica o campo. */
  getRouteTransferDisplay(m: StockMovement): string {
    const raw = (m.transferStatus || '').trim();
    if (raw) {
      return this.getTransferStatusViewLabel(m.transferStatus);
    }
    if (this.stockMovementsService.isEntryInTransitPhase(m)) {
      return 'Em trânsito';
    }
    return '—';
  }

  getViewStatusBadgeVariant(m: StockMovement): 'amber' | 'emerald' | 'red' | 'slate' {
    const s = m.status;
    const sLower = (s || '').toLowerCase();
    if (
      s === 'AwaitingReceipt' ||
      sLower === 'recparcialpend' ||
      sLower === 'aguardandocad' ||
      (sLower.includes('aguardando') && sLower.includes('cad'))
    ) {
      return 'amber';
    }
    if (s === 'Executed' || s === 'Received' || s === 'ReceiptCompleted' || (s || '').toLowerCase() === 'receiptcompleted') {
      return 'emerald';
    }
    if (s === 'Cancelled' || s === 'Rejected') {
      return 'red';
    }
    return 'slate';
  }

  isWarehouseUninformed(value?: string | null): boolean {
    const v = (value ?? '').trim();
    return !v || v === '-';
  }

  /** Exibe faixa de ação «Vincular item» na visualização da movimentação. */
  canShowLinkPendingItemBanner(m: StockMovement | null): boolean {
    return this.isViewMode && !!m && canLinkPendingItemToStockEntry(m);
  }

  openLinkItemModal(): void {
    const m = this.movement;
    if (!m) return;
    const pendingId = resolvePendingItemEntryIdForLink(m);
    if (pendingId == null) return;
    this.linkItemModalPendingEntryId = pendingId;
    this.linkItemModalContext = `${m.itemName || 'Entrada'} — mov. #${m.id}`;
    this.linkItemSelectedId = null;
    this.linkItemSearchTerm = '';
    this.linkItemSearchResults = [];
    this.showLinkItemModal = true;
  }

  closeLinkItemModal(): void {
    if (this.linkItemSubmitting) return;
    this.showLinkItemModal = false;
    this.linkItemModalPendingEntryId = null;
  }

  searchLinkItems(): void {
    const q = (this.linkItemSearchTerm || '').trim();
    if (q.length < 2) {
      this.errorService.showInfoMessage('Digite ao menos 2 caracteres para buscar o item.');
      return;
    }
    this.linkItemSearchLoading = true;
    this.itemService
      .getAllItemsWithNameFilter(q, { page: 1, pageSize: 20 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.linkItemSearchResults = res.items || [];
          this.linkItemSearchLoading = false;
        },
        error: () => {
          this.linkItemSearchResults = [];
          this.linkItemSearchLoading = false;
        }
      });
  }

  selectLinkSearchItem(it: Item): void {
    this.linkItemSelectedId = it.id;
    this.linkItemSearchTerm = `${it.code} — ${it.name}`;
  }

  clearLinkItemSelection(): void {
    this.linkItemSelectedId = null;
    this.linkItemSearchTerm = '';
    this.linkItemSearchResults = [];
  }

  submitLinkPendingItem(): void {
    const pid = this.linkItemModalPendingEntryId;
    const iid = this.linkItemSelectedId;
    if (pid == null || iid == null || iid <= 0) {
      this.errorService.showErrorMessage('Selecione um item de estoque na lista.');
      return;
    }
    this.linkItemSubmitting = true;
    this.stockMovementsService
      .linkPendingItemRequest(pid, { itemId: iid })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updated) => {
          this.linkItemSubmitting = false;
          this.closeLinkItemModal();
          this.errorService.showSuccessMessage('Item vinculado com sucesso.');
          if (updated) {
            this.movement = updated;
            this.populateForm(updated);
          } else {
            this.loadMovement();
          }
        },
        error: () => {
          this.linkItemSubmitting = false;
        }
      });
  }

  private mapMovementTypeToLabel(apiType: string): 'Entrada' | 'Saida' {
    return apiType === 'Exit' ? 'Saida' : 'Entrada';
  }

  private mapMovementTypeToApi(labelType: 'Entrada' | 'Saida'): 'Entry' | 'Exit' {
    return labelType === 'Saida' ? 'Exit' : 'Entry';
  }
}